/**
 * taskStore.js
 * ─────────────
 * Persistent task store for joint assessments. Every task has:
 *   id, assessmentId, title, type, assignee, dueDate, status, createdAt,
 *   createdBy, completedAt, completedBy
 *
 * Tasks are auto-assigned via TASK_ROUTING in rbac.js when no explicit
 * assignee is given. Status changes are committed immediately for
 * non-sensitive transitions; closing a task on a closed assessment routes
 * through the approval queue.
 */

import { suggestAssignee, TASK_ROUTING } from './rbac';
import { listUsers, getUser } from './userDirectory';

const STORAGE_KEY = 'clearequal.tasks.v1';
const EVENT_NAME = 'clearequal.tasks.changed';

const seedTasks = () => [
  // Pre-seeded tasks for the consumer-goods crisis scenario so the demo
  // has visible content from the first second.
  {
    id: 'task-seed-1',
    assessmentId: 'JA-2025-001',
    title: 'Review salary data for affected positions',
    type: 'data-review',
    assignee: 'sarah.chen',
    department: 'Engineering',
    dueDate: '2025-01-15',
    status: 'completed',
    createdAt: '2025-01-04T09:00:00Z',
    createdBy: 'admin@clearequal.com',
    completedAt: '2025-01-10T14:22:00Z',
    completedBy: 'sarah.chen',
    autoAssigned: true,
  },
  {
    id: 'task-seed-2',
    assessmentId: 'JA-2025-001',
    title: 'Conduct market analysis for comparable roles',
    type: 'market-analysis',
    assignee: 'michael.rodriguez',
    department: 'Compensation',
    dueDate: '2025-01-20',
    status: 'in-progress',
    createdAt: '2025-01-08T10:30:00Z',
    createdBy: 'admin@clearequal.com',
    autoAssigned: true,
  },
  {
    id: 'task-seed-3',
    assessmentId: 'JA-2025-001',
    title: 'Prepare remediation proposal',
    type: 'remediation-plan',
    assignee: 'emma.thompson',
    department: 'Engineering',
    dueDate: '2025-01-25',
    status: 'pending',
    createdAt: '2025-01-12T11:15:00Z',
    createdBy: 'admin@clearequal.com',
    autoAssigned: true,
  },
];

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeAll(tasks) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  } catch {
    /* quota exceeded, silently swallow */
  }
  // Notify subscribers (in-tab + cross-tab via storage event)
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {
    /* */
  }
}

function ensureSeeded() {
  const cur = readAll();
  if (cur) return cur;
  const seed = seedTasks();
  writeAll(seed);
  return seed;
}

export function getTasks(assessmentId = null) {
  const all = ensureSeeded();
  if (!assessmentId) return all;
  return all.filter((t) => t.assessmentId === assessmentId);
}

/**
 * Create a new task. If `assignee` is omitted, the system auto-routes
 * based on `type` and `department` using rbac.suggestAssignee. The actual
 * user object (with name/email) is resolved at read time.
 *
 * Returns the created task (with assignedAutomatically flag set if relevant).
 */
export function createTask({
  assessmentId,
  title,
  type = 'general',
  assignee = null,
  department = null,
  dueDate,
  createdBy,
  tenantId = null,
}) {
  const all = ensureSeeded();

  let assigneeId = assignee;
  let autoAssigned = false;

  if (!assigneeId) {
    const users = listUsers(tenantId);
    const suggested = suggestAssignee(type, department, users);
    if (suggested) {
      assigneeId = suggested.id;
      autoAssigned = true;
    }
  }

  const task = {
    id: `task-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    assessmentId,
    title,
    type,
    assignee: assigneeId,
    department,
    dueDate,
    status: 'pending',
    createdAt: new Date().toISOString(),
    createdBy,
    autoAssigned,
  };

  all.push(task);
  writeAll(all);
  return task;
}

/**
 * Update task fields (e.g. mark complete, change assignee).
 * Returns the updated task or null if not found.
 */
export function updateTask(taskId, patch, actor) {
  const all = ensureSeeded();
  const idx = all.findIndex((t) => t.id === taskId);
  if (idx === -1) return null;

  const before = all[idx];
  const updated = { ...before, ...patch };

  // If transitioning to completed, stamp completion fields
  if (patch.status === 'completed' && before.status !== 'completed') {
    updated.completedAt = new Date().toISOString();
    updated.completedBy = actor || updated.assignee;
  }

  all[idx] = updated;
  writeAll(all);
  return updated;
}

/**
 * Subscribe to task store changes. Returns an unsubscribe function.
 */
export function subscribe(callback) {
  const handler = () => callback(getTasks());
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener('storage', handler);
  };
}

/**
 * Lookup helper: enrich a task with the assignee's display name and email.
 */
export function getAssigneeDetails(assigneeId) {
  if (!assigneeId) return null;
  return getUser(assigneeId);
}
